import { CommonModule } from '@angular/common';
import { AfterViewChecked, Component, ElementRef, inject, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateModule } from '@ngx-translate/core';
import { DateTime } from 'luxon';

import { StateYearComponent } from './state-year/state-year.component';

import { ReferenceMonthService } from '@services/reference-month.service';
import { SalaryIndexService } from '@services/salary-index.service';

import { Contract } from '@classes/contract/contract';
import { ContractState } from '@classes/contract/contract-state';
import { Fte } from '@classes/contract/fte';
import { StatesByYear } from '@classes/contract/states-by-year';
import { SalaryIndex } from '@classes/parameters/conjonctural-parameters/salary-index';

@Component({
    selector: 'app-contract-state',
    templateUrl: './contract-state.component.html',
    styleUrls: ['./contract-state.component.scss'],
    imports: [
        CommonModule,
        TranslateModule,
        MatIcon,
        StateYearComponent
    ]
})
export class ContractStateComponent implements OnChanges, AfterViewChecked {
    @Input() contract: Contract;
    @ViewChild(MatSort, { static: true }) sort: MatSort;

    @ViewChild('contractState') contractState: ElementRef;

    currentYear: number;
    statesByYear: StatesByYear[];
    salaryIndexes: SalaryIndex[];

    dataSource: MatTableDataSource<ContractState>;
    displayedColumns: string[] = [
        'dueMonth',
        'scalePayment',
        'superiorScalePayment',
        'parallelScalePayment',
        'scaleSeniority',
        'professionalStatus',
        'scaleAdministrative',
        'scaleAdministrativeSeniority'
    ];

    dateFormat: 'yyyy-MM';
    completeDateFormat: 'dd-MM-yyyy';

    private canScroll: boolean;

    private readonly refMonthSrv = inject(ReferenceMonthService);
    private readonly salaryIndexSrv = inject(SalaryIndexService);

    constructor() { }

    ngOnChanges(changes: SimpleChanges): void {
        this.currentYear = DateTime.fromISO(this.refMonthSrv.refMonth).year;
        if (changes.contract.previousValue === changes.contract.currentValue) {
            this.canScroll = false;
        } else {
            if (this.salaryIndexes?.length) {
                this.initStates();
            } else {
                this.salaryIndexSrv.get()
                    .subscribe((indexes: SalaryIndex[]) => {
                        this.salaryIndexes = indexes;
                        this.initStates();
                    });
            }
            this.canScroll = true;
        }
    }

    ngAfterViewChecked(): void {
        if (this.canScroll && this.contractState) {
            this.contractState.nativeElement.scrollIntoView();
            this.canScroll = false;
        }
    }

    togglestates(states: StatesByYear): void {
        states.isExpanded = !states.isExpanded;
    }

    private initStates(): void {
        const uniqueYears = Array.from(new Set(this.contract.contractStates.map((state: ContractState) => DateTime.fromISO(state.dueMonth).year)));
        const budgetaryFte = this.contract.fte.filter((f: Fte) => f.code === 'BudgetaryFte');
        const contractFte = this.contract.fte.filter((f: Fte) => f.code === 'ContractFte');
        this.statesByYear = uniqueYears.map(year => {
            const contractStates = this.contract.contractStates.filter((contractState: ContractState) => {
                const budFte = budgetaryFte.filter(fte => fte.month === contractState.dueMonth);
                contractState.budgetaryFte = budFte.reduce((accumulator, current) => accumulator + current.value, 0);
                const contFte = contractFte.filter(fte => fte.month === contractState.dueMonth);
                contractState.contractFte = contFte.reduce((accumulator, current) => accumulator + current.value, 0);
                contractState.indexValue = this.salaryIndexes.find((index: SalaryIndex) => index.fromDueMonth <= contractState.dueMonth).indexValue;
                return DateTime.fromISO(contractState.dueMonth).year === year;
            }).sort((a: ContractState, b: ContractState) => {
                return a.dueMonth < b.dueMonth ? 1 : a.dueMonth > b.dueMonth ? -1 : 0;
            });
            return new StatesByYear(
                contractStates,
                year === this.currentYear,
                year
            );
        }).sort((a, b) => b.year - a.year);
    }
}
